/**
 * Brand data for Shop by Brand section - Firebase-ready structure
 * Contains detailed brand information including history and certifications
 */
import { Brand } from '@/types';
import { Timestamp } from 'firebase/firestore';

// Helper to create mock timestamps
const createTimestamp = (): Timestamp => {
  return {
    seconds: Math.floor(Date.now() / 1000),
    nanoseconds: 0,
    toDate: () => new Date(),
    toMillis: () => Date.now(),
    isEqual: () => false,
    valueOf: () => '',
  } as unknown as Timestamp;
};

// ==========================================
// TYPE DEFINITIONS
// ==========================================

export interface BrandFeature {
  icon: string;
  title: string;
  description: string;
}

export interface BrandData {
  id: Brand;
  name: string;
  slug: string;
  description: string;
  fullDescription: string;
  logo: string;
  image: string;
  bannerImage: string;
  productCount: number;
  color: string;
  tagline: string;
  // Enhanced fields
  foundedYear: number;
  headquarters: string;
  country: string;
  specializations: string[];
  keyProducts: string[];
  certifications: string[];
  features: BrandFeature[];
  website?: string;
  isActive: boolean;
  isFeatured: boolean;
  order: number;
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

// ==========================================
// MOCK DATA
// ==========================================

export const brands: BrandData[] = [
  {
    id: Brand.THREE_STAR,
    name: '3Star',
    slug: '3star',
    description: 'Quality dental disposables and accessories for everyday dental practice.',
    fullDescription: '3Star is a leading manufacturer of dental disposables, known for producing high-quality suction tips, protective gear, trays, and storage solutions. With a focus on quality materials and innovative designs, 3Star products are trusted by dental professionals across Asia. Their commitment to using medical-grade materials ensures safety and reliability in every product.',
    logo: '/images/brands/3star-logo.png',
    image: '/images/brands/3star-banner.jpg',
    bannerImage: '/images/brands/3star-hero.jpg',
    productCount: 20,
    color: '#0077B6',
    tagline: 'Quality Disposables for Daily Practice',
    foundedYear: 2005,
    headquarters: 'Mumbai, India',
    country: 'India',
    specializations: [
      'Suction Tips',
      'Protective Equipment',
      'Storage Solutions',
      'Instrument Trays',
      'Hygiene Disposables',
    ],
    keyProducts: [
      'Suction Tips with Copper Wire',
      'Retainer & Denture Boxes',
      'Instrument Trays',
      '3-Ply Face Masks',
      'Cotton Roll Dispensers',
    ],
    certifications: ['ISO 9001:2015', 'CE Marked', 'Medical Grade Materials'],
    features: [
      {
        icon: 'FiShield',
        title: 'Medical Grade',
        description: 'All products made from certified medical-grade materials',
      },
      {
        icon: 'FiCheckCircle',
        title: 'Quality Tested',
        description: 'Rigorous quality control on every batch',
      },
      {
        icon: 'FiPackage',
        title: 'Bulk Packaging',
        description: 'Cost-effective bulk packaging options',
      },
    ],
    website: 'https://3stardental.com',
    isActive: true,
    isFeatured: true,
    order: 1,
    createdAt: createTimestamp(),
    updatedAt: createTimestamp(),
  },
  {
    id: Brand.HAGEN,
    name: 'Hagen',
    slug: 'hagen',
    description: 'Premium handpieces and implant instruments with German precision engineering.',
    fullDescription: 'Hagen represents the pinnacle of dental handpiece technology, combining German engineering excellence with innovative design. Specializing in high-speed and low-speed handpieces, Hagen products are known for their durability, precision, and ergonomic design. Their implant instruments are designed for surgical precision, featuring advanced irrigation systems and torque control.',
    logo: '/images/brands/hagen-logo.png',
    image: '/images/brands/hagen-banner.jpg',
    bannerImage: '/images/brands/hagen-hero.jpg',
    productCount: 9,
    color: '#00B4D8',
    tagline: 'Precision Engineering for Dental Excellence',
    foundedYear: 1998,
    headquarters: 'Stuttgart, Germany',
    country: 'Germany',
    specializations: [
      'High-Speed Handpieces',
      'Low-Speed Handpieces',
      'Implant Instruments',
      'Surgical Equipment',
      'LED Handpieces',
    ],
    keyProducts: [
      'Push Button High-Speed Handpiece',
      'LED Illuminated Handpiece',
      'Implant Handpiece 20:1',
      'Contra Angle Handpiece',
      'Straight Handpiece',
    ],
    certifications: ['ISO 13485', 'CE Medical Device', 'FDA Registered', 'German TÜV'],
    features: [
      {
        icon: 'FiZap',
        title: 'High Performance',
        description: 'Up to 300,000 RPM with ceramic bearings',
      },
      {
        icon: 'FiDroplet',
        title: 'Advanced Cooling',
        description: '4-hole spray for optimal cooling',
      },
      {
        icon: 'FiRefreshCw',
        title: 'Autoclavable',
        description: 'Fully autoclavable at 135°C',
      },
    ],
    website: 'https://hagen-dental.com',
    isActive: true,
    isFeatured: true,
    order: 2,
    createdAt: createTimestamp(),
    updatedAt: createTimestamp(),
  },
  {
    id: Brand.DEXELL,
    name: 'Dexell',
    slug: 'dexell',
    description: 'German technology endodontic files and diamond burs for superior performance.',
    fullDescription: 'Dexell brings German precision to endodontic instruments, specializing in rotary files, hand files, and diamond burs. Their heat-treated NiTi files offer exceptional flexibility and cutting efficiency, while their diamond burs feature multi-layered matrix for extended life. Dexell products are designed for demanding clinical requirements where precision matters.',
    logo: '/images/brands/dexell-logo.png',
    image: '/images/brands/dexell-banner.jpg',
    bannerImage: '/images/brands/dexell-hero.jpg',
    productCount: 5,
    color: '#DC2626',
    tagline: 'German Technology for Precise Endodontics',
    foundedYear: 2008,
    headquarters: 'Berlin, Germany',
    country: 'Germany',
    specializations: [
      'Rotary Endodontic Files',
      'Hand Files',
      'Diamond Burs',
      'K-Files',
      'C+ Files',
    ],
    keyProducts: [
      'DXL-Pro Rotary Files',
      'NiTi Hand Files',
      'K-Files Stainless Steel',
      'C-Plus Files',
      'Diamond Burs',
    ],
    certifications: ['ISO 13485', 'CE Marked', 'German Quality Standards'],
    features: [
      {
        icon: 'FiTarget',
        title: 'Precision Cut',
        description: 'Heat-treated for optimal flexibility',
      },
      {
        icon: 'FiTrendingUp',
        title: 'Efficient',
        description: '2-3 files for most cases',
      },
      {
        icon: 'FiShield',
        title: 'Safety First',
        description: 'Non-cutting safety tips',
      },
    ],
    website: 'https://dexell.com',
    isActive: true,
    isFeatured: true,
    order: 3,
    createdAt: createTimestamp(),
    updatedAt: createTimestamp(),
  },
  {
    id: Brand.NEXEN,
    name: 'Nexen',
    slug: 'nexen',
    description: 'Next generation dental sterilization equipment and instruments.',
    fullDescription: 'Nexen specializes in dental sterilization and infection control equipment. From manual autoclaves to UV chambers and glass bead sterilizers, Nexen provides comprehensive solutions for maintaining the highest standards of hygiene in dental practices. Their equipment is designed for durability, ease of use, and reliable performance.',
    logo: '/images/brands/nexen-logo.png',
    image: '/images/brands/nexen-banner.jpg',
    bannerImage: '/images/brands/nexen-hero.jpg',
    productCount: 4,
    color: '#059669',
    tagline: 'Advanced Sterilization Solutions',
    foundedYear: 2010,
    headquarters: 'Ahmedabad, India',
    country: 'India',
    specializations: [
      'Autoclaves',
      'UV Sterilization',
      'Glass Bead Sterilizers',
      'Sterilization Accessories',
      'Infection Control',
    ],
    keyProducts: [
      'Manual Autoclave (11L/21L)',
      'UV Chamber with 12 Trays',
      'Glass Bead Sterilizer',
      'Autoclave Drums',
    ],
    certifications: ['ISO 9001:2015', 'CE Marked', 'BIS Certified'],
    features: [
      {
        icon: 'FiThermometer',
        title: 'Precise Control',
        description: 'Accurate temperature and pressure control',
      },
      {
        icon: 'FiClock',
        title: 'Time Efficient',
        description: 'Quick sterilization cycles',
      },
      {
        icon: 'FiSettings',
        title: 'Easy Operation',
        description: 'User-friendly controls',
      },
    ],
    website: 'https://nexen-dental.com',
    isActive: true,
    isFeatured: true,
    order: 4,
    createdAt: createTimestamp(),
    updatedAt: createTimestamp(),
  },
  {
    id: Brand.PROXEE,
    name: 'Proxee',
    slug: 'proxee',
    description: 'Innovative oral care products including portable water flossers.',
    fullDescription: 'Proxee is at the forefront of personal oral care innovation, developing portable, rechargeable devices for professional-grade oral hygiene at home. Their flagship water flosser combines portability with powerful performance, featuring multiple cleaning modes and interchangeable nozzles for comprehensive oral care.',
    logo: '/images/brands/proxee-logo.png',
    image: '/images/brands/proxee-banner.jpg',
    bannerImage: '/images/brands/proxee-hero.jpg',
    productCount: 1,
    color: '#7C3AED',
    tagline: 'Smart Oral Care Solutions',
    foundedYear: 2019,
    headquarters: 'Shenzhen, China',
    country: 'China',
    specializations: [
      'Water Flossers',
      'Oral Irrigators',
      'Portable Dental Devices',
      'Oral Hygiene Products',
    ],
    keyProducts: [
      'Proxee Water Flosser PX555',
    ],
    certifications: ['CE Marked', 'FCC Certified', 'IPX7 Waterproof'],
    features: [
      {
        icon: 'FiBattery',
        title: 'Long Battery',
        description: '1200mAh fast-charging battery',
      },
      {
        icon: 'FiDroplet',
        title: 'IPX7 Waterproof',
        description: 'Full body waterproof design',
      },
      {
        icon: 'FiSliders',
        title: '3 Modes',
        description: 'Soft, Standard, and Power modes',
      },
    ],
    website: 'https://proxee.com',
    isActive: true,
    isFeatured: false,
    order: 5,
    createdAt: createTimestamp(),
    updatedAt: createTimestamp(),
  },
  {
    id: Brand.SUNVIVO,
    name: 'Sunvivo',
    slug: 'sunvivo',
    description: 'Our house brand offering quality dental equipment and supplies.',
    fullDescription: 'Sunvivo is our proprietary brand, developed to provide dental professionals with quality products at competitive prices. By working directly with certified manufacturers, we ensure that Sunvivo products meet the same quality standards as international brands while offering better value.',
    logo: '/images/brands/sunvivo-logo.png',
    image: '/images/brands/sunvivo-banner.jpg',
    bannerImage: '/images/brands/sunvivo-hero.jpg',
    productCount: 0,
    color: '#BE123C',
    tagline: 'Quality You Can Trust',
    foundedYear: 2014,
    headquarters: 'Gurgaon, India',
    country: 'India',
    specializations: [
      'General Dental Supplies',
      'Consumables',
      'Basic Equipment',
    ],
    keyProducts: [],
    certifications: ['ISO 9001:2015', 'CE Marked'],
    features: [
      {
        icon: 'LuIndianRupee',
        title: 'Value',
        description: 'Quality products at competitive prices',
      },
      {
        icon: 'FiCheckCircle',
        title: 'Trusted',
        description: 'Direct quality control',
      },
      {
        icon: 'FiTruck',
        title: 'Support',
        description: 'Full warranty and support',
      },
    ],
    website: 'https://sunvivo.in',
    isActive: false,
    isFeatured: false,
    order: 6,
    createdAt: createTimestamp(),
    updatedAt: createTimestamp(),
  },
];

// ==========================================
// HELPER FUNCTIONS
// ==========================================

// Get brand by slug
export const getBrandBySlug = (slug: string): BrandData | undefined => {
  return brands.find(b => b.slug === slug);
};

// Get brand by ID
export const getBrandById = (id: Brand): BrandData | undefined => {
  return brands.find(b => b.id === id);
};

// Get active brands (with products)
export const getActiveBrands = (): BrandData[] => {
  return brands
    .filter(b => b.isActive && b.productCount > 0)
    .sort((a, b) => a.order - b.order);
};

// Get featured brands
export const getFeaturedBrands = (): BrandData[] => {
  return brands
    .filter(b => b.isFeatured && b.isActive)
    .sort((a, b) => a.order - b.order);
};

// Get all brands
export const getAllBrands = (): BrandData[] => {
  return [...brands].sort((a, b) => a.order - b.order);
};

// Get brands by country
export const getBrandsByCountry = (country: string): BrandData[] => {
  return brands.filter(b => b.country === country && b.isActive);
};
